import { Form } from "@/components/ui/form";
import { DynamicFields } from "@/features/FormCard/DynamicCard";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  AutocompleteAsyncField,
  AutocompleteLabelField,
  InputField,
  SelectFieldWithSection,
} from "@/features/FormCard/Fields";

import { useDynamicForm } from "@/features/Edit/dynamic-edit-form";
import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { NewDiff } from "@/features/FormCard/NewDiff";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { useCurrencyOptions } from "@/hooks/useCurrencyOption";
import { Label } from "@radix-ui/react-label";
import {
  dynamicFormFields,
  dynamicNewFormConfigWithoutId,
} from "./edit-form-conf";
import { fieldTranslations } from "./translations";

const client = Clients.C5;
const table_name = Tables.C5_STATIC_DATA;

export function C5StaticDataNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    selectFieldsConfig,
    isLoadingTranslations,
    optionsMap,
    options,
  } = useDynamicForm({
    client,
    table_name,
    formConfig: dynamicNewFormConfigWithoutId,
    isNew: true,
  });

  const assetClass1 = form.watch("asset_class_1");
  const assetClass2 = form.watch(dynamicFormFields.asset_class_2);

  const currencyOptions = useCurrencyOptions();

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full">
      <div className="flex justify-center">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Actif
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <Label>
                  {t(`${table_name}.sections.general_information`, {
                    ns: "tables",
                    defaultValue: "Informations générales",
                  })}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-8">
                    <InputField
                      name={dynamicFormFields.name}
                      label={t(`${table_name}.columns.name`, {
                        ns: "tables",
                        defaultValue: "Nom",
                      })}
                      isRequired={true}
                      capitalize={true}
                      control={form.control}
                    />
                  </div>
                  <div className="md:col-span-4">
                    <AutocompleteLabelField
                      name={dynamicFormFields.currency}
                      label={t(`${table_name}.columns.currency`, {
                        ns: "tables",
                        defaultValue: "Devise",
                      })}
                      options={currencyOptions}
                      isRequired={true}
                      control={form.control}
                      className="min-w-0"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                  <InputField
                    name={dynamicFormFields.isin}
                    label={t(`${table_name}.columns.isin`, {
                      ns: "tables",
                      defaultValue: "ISIN",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name={dynamicFormFields.code}
                    label={t(`${table_name}.columns.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name={dynamicFormFields.ticker}
                    label={t(`${table_name}.columns.ticker`, {
                      ns: "tables",
                      defaultValue: "Ticker",
                    })}
                    control={form.control}
                  />
                </div>
                <AutocompleteAsyncField
                  name={dynamicFormFields.c5_issuer_name}
                  label={t(`${table_name}.columns.c5_issuer_name`, {
                    ns: "tables",
                    defaultValue: "Emetteur",
                  })}
                  control={form.control}
                  base_url={`${Clients.C5}/${Tables.C5_ISSUERS}`}
                  id_column="issuer_id"
                  column_name={dynamicFormFields.name}
                />
                <SelectFieldWithSection
                  listboxWrapperClassNames="!h-[450px] !max-h-none"
                  primary_key="asset_class_1"
                  name={dynamicFormFields.asset_class_2}
                  label={t(`${table_name}.columns.name`, {
                    ns: "tables",
                    defaultValue: "Classe d'actifs",
                  })}
                  control={form.control}
                  sections={optionsMap[dynamicFormFields.asset_class_2]}
                  placeholder={t("form.placeholders.select")}
                  isRequired
                />
                <Divider />
                <Label>
                  {t("sections.technical_info", {
                    defaultValue: "Informations techniques",
                  })}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={dynamicFormFields.strategy_id}
                    label={t(`${table_name}.columns.strategy_id`, {
                      ns: "tables",
                      defaultValue: "ID de la stratégie",
                    })}
                    control={form.control}
                    type="number"
                  />
                </div>
                <DynamicFields
                  primaryValue={assetClass1}
                  fieldValue={assetClass2}
                  control={form.control}
                  config={selectFieldsConfig?.[dynamicFormFields.asset_class_2]}
                  isLoadingTranslations={isLoadingTranslations}
                  options={options}
                />
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
          children={
            changedFields
              ? NewDiff(
                  changedFields,
                  ["issuer_id"],
                  fieldTranslations,
                  table_name,
                )
              : t("no_changes")
          }
        />

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
